/**
 * Karte mit den eigenen Urlaubsantraegen des aktuellen Benutzers.
 * Wird im "Antraege"-Tab oberhalb der Genehmigungs-Queue angezeigt.
 *
 * Zeigt den Namen des angemeldeten Mitarbeiters sowie pro Antrag:
 *   - Datum, Tage, Urlaubstyp, Feiertage gespart
 *   - Status-Badge (pending/approved/rejected)
 *   - Notiz (falls vorhanden)
 *   - Loeschen/Stornieren-Button (pending/approved)
 *   - Ablehnungs-Grund (rejected)
 */
import React, { useEffect, useState, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { format, parseISO } from 'date-fns';
import { de } from 'date-fns/locale';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Skeleton } from '../../components/ui/skeleton';
import { Calendar as CalendarIcon, Trash2, User, CalendarX } from 'lucide-react';
import { vacationsAPI, employeesAPI } from '../../lib/api';
import { toast } from 'sonner';
import { RemoveDaysDialog } from './RemoveDaysDialog';

const STATUS_BADGE = {
  pending: { label: 'Ausstehend', cls: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-200' },
  approved: { label: 'Genehmigt', cls: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-200' },
  rejected: { label: 'Abgelehnt', cls: 'bg-rose-100 text-rose-800 dark:bg-rose-900/30 dark:text-rose-200' },
  cancelled: { label: 'Storniert', cls: 'bg-muted text-muted-foreground' },
};

function StatusBadge({ status }) {
  const cfg = STATUS_BADGE[status] || { label: status, cls: 'bg-muted' };
  return <Badge variant="secondary" className={cfg.cls}>{cfg.label}</Badge>;
}

export function MyVacationsCard() {
  const [me, setMe] = useState(null);
  const [vacations, setVacations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editDaysVacation, setEditDaysVacation] = useState(null);

  // Deep-Link Handling
  const [searchParams] = useSearchParams();
  const focusVacationId = searchParams.get('antrag');
  const [highlightId, setHighlightId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const meRes = await employeesAPI.getMyProfile();
      setMe(meRes.data);
      const vacRes = await vacationsAPI.getAll({ employee_id: meRes.data.id });
      setVacations(vacRes.data || []);
    } catch (err) {
      // Mitarbeiter ohne Profil bekommen leere Karte (kein Fehler-Toast)
      setVacations([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Scroll + Highlight wenn Deep-Link gesetzt
  useEffect(() => {
    if (!focusVacationId || loading) return;
    const target = document.querySelector(`[data-my-vacation-card="${focusVacationId}"]`);
    if (!target) return;
    target.scrollIntoView({ behavior: 'smooth', block: 'center' });
    setHighlightId(focusVacationId);
    const t = setTimeout(() => setHighlightId(null), 2200);
    return () => clearTimeout(t);
  }, [focusVacationId, loading, vacations]);

  const handleDelete = async (vacation) => {
    const confirmMsg = vacation.status === 'approved'
      ? 'Genehmigten Urlaub stornieren?'
      : 'Urlaubsantrag löschen?';
    if (!window.confirm(confirmMsg)) return;
    try {
      await vacationsAPI.delete(vacation.id);
      toast.success(vacation.status === 'approved' ? 'Urlaub storniert' : 'Antrag gelöscht');
      load();
    } catch (err) {
      toast.error('Fehler — bitte erneut versuchen');
    }
  };

  if (loading) {
    return (
      <Card className="border-border/60">
        <CardHeader><Skeleton className="h-6 w-40" /></CardHeader>
        <CardContent><Skeleton className="h-24 w-full" /></CardContent>
      </Card>
    );
  }
  if (!me) return null;

  const myName = `${me.last_name || ''}, ${me.first_name || ''}`.replace(/^,\s*/, '');

  return (
    <Card className="border-border/60" data-testid="my-vacations-card">
      <CardHeader className="border-b border-border/40 pb-3">
        <CardTitle className="font-heading text-lg flex items-center gap-2">
          <div className="p-1.5 rounded-lg bg-primary/10">
            <User className="h-4 w-4 text-primary" />
          </div>
          <span>Meine Anträge</span>
          <span className="text-sm font-normal text-muted-foreground" data-testid="my-vacations-name">— {myName}</span>
          <Badge variant="outline" className="ml-auto" data-testid="my-vacations-count">
            {vacations.length}
          </Badge>
        </CardTitle>
      </CardHeader>
      <CardContent className="p-4 max-h-[400px] overflow-y-auto">
        {vacations.length > 0 ? (
          <div className="space-y-3">
            {vacations.map((vacation) => (
              <div
                key={vacation.id}
                className={`p-3 rounded-lg bg-secondary/30 space-y-2 transition-all duration-500 ${
                  highlightId === vacation.id
                    ? 'ring-2 ring-amber-500 ring-offset-2 shadow-lg shadow-amber-500/20 scale-[1.01]'
                    : ''
                }`}
                data-testid={`my-vacation-item-${vacation.id}`}
                data-my-vacation-card={vacation.id}
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="text-xs text-muted-foreground mb-1" data-testid={`my-vacation-name-${vacation.id}`}>
                      {myName}
                    </div>
                    <p className="font-medium text-sm">
                      {format(parseISO(vacation.start_date), 'dd.MM.', { locale: de })}
                      {' – '}
                      {format(parseISO(vacation.end_date), 'dd.MM.yyyy', { locale: de })}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {vacation.days_count} Tag{vacation.days_count !== 1 ? 'e' : ''} • {vacation.vacation_type}
                      {vacation.holidays_saved > 0 && (
                        <span className="text-green-600 ml-1">
                          ({vacation.holidays_saved} Feiertag{vacation.holidays_saved > 1 ? 'e' : ''} gespart)
                        </span>
                      )}
                    </p>
                  </div>
                  <StatusBadge status={vacation.status} />
                </div>
                {vacation.notes && (
                  <p className="text-xs text-muted-foreground">{vacation.notes}</p>
                )}
                {(vacation.status === 'pending' || vacation.status === 'approved') && (
                  <div className="flex items-center gap-1">
                    {vacation.days_count > 1 && (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-7"
                        onClick={() => setEditDaysVacation(vacation)}
                        data-testid={`my-vacation-edit-days-${vacation.id}`}
                      >
                        <CalendarX className="h-3 w-3 mr-1" />
                        Tage bearbeiten
                      </Button>
                    )}
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-7 text-destructive hover:text-destructive"
                      onClick={() => handleDelete(vacation)}
                      data-testid={`my-vacation-delete-${vacation.id}`}
                    >
                      <Trash2 className="h-3 w-3 mr-1" />
                      {vacation.status === 'approved' ? 'Stornieren' : 'Löschen'}
                    </Button>
                  </div>
                )}
                {vacation.status === 'rejected' && vacation.rejection_reason && (
                  <p className="text-xs text-destructive">Grund: {vacation.rejection_reason}</p>
                )}
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-6 text-muted-foreground">
            <CalendarIcon className="h-10 w-10 mx-auto mb-2 opacity-50" />
            <p className="text-sm">Keine Urlaubsanträge</p>
          </div>
        )}
      </CardContent>

      <RemoveDaysDialog
        open={!!editDaysVacation}
        onOpenChange={(o) => { if (!o) setEditDaysVacation(null); }}
        vacation={editDaysVacation}
        onDone={load}
      />
    </Card>
  );
}
